import { useState, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { BsTelephoneInbound, BsTelephoneOutbound } from 'react-icons/bs';

import { usePhoneLookup } from '../../models/phoneLookup/queries';
import { useAircallEverywhereStore } from '../../stores/aircallEverywhereStore';
import { usePowerDialerStore } from '../../stores/powerDialerStore';

import { getCallLabel, getDisplayNumber, getDurationText, groupLookupParties } from './callOverlayUtils';
import ExpandedCallOverlay from './ExpandedCallOverlay';
import MinimizedCallOverlay from './MinimizedCallOverlay';

/**
 * Floating call overlay visible across all CRM pages during active calls.
 * Hidden when PowerDialer is active (it has its own call status UI).
 */
const AircallCallOverlay = () => {
	const callState = useAircallEverywhereStore((s) => s.callState);
	const activeCall = useAircallEverywhereStore((s) => s.activeCall);
	const overlayMinimized = useAircallEverywhereStore((s) => s.overlayMinimized);
	const setOverlayMinimized = useAircallEverywhereStore((s) => s.setOverlayMinimized);
	const widgetVisible = useAircallEverywhereStore((s) => s.widgetVisible);
	const setWidgetVisible = useAircallEverywhereStore((s) => s.setWidgetVisible);

	const dialerActive = usePowerDialerStore((s) => s.active);
	const dialerMinimized = usePowerDialerStore((s) => s.minimized);

	const navigate = useNavigate();

	// Resolve the caller (inbound only) to CRM contacts/companies. Stays disabled for
	// outbound calls and when no caller number is known.
	const isInbound = activeCall?.direction === 'inbound';
	const { data: lookup } = usePhoneLookup(activeCall?.from, {
		enabled: callState !== 'idle' && isInbound,
	});

	const [elapsed, setElapsed] = useState(0);
	const timerRef = useRef(null);

	// Talk-time timer: runs only once the call is answered (answeredAt is set). While the call
	// is merely ringing there is no duration to count — the overlay shows "Klingelt…" instead.
	useEffect(() => {
		if (callState === 'active' && activeCall?.answeredAt) {
			setElapsed(Date.now() - activeCall.answeredAt);
			timerRef.current = setInterval(() => {
				setElapsed(Date.now() - activeCall.answeredAt);
			}, 1000);
		} else {
			setElapsed(0);
		}
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, [callState, activeCall?.answeredAt]);

	if (callState === 'idle') return null;
	if (dialerActive && !dialerMinimized) return null;

	const displayNumber = getDisplayNumber(activeCall);
	const label = getCallLabel(callState, activeCall?.direction);
	const isRinging = callState === 'ringing_inbound' || callState === 'ringing_outbound';
	const isActive = callState === 'active';
	const isEnded = callState === 'ended';
	const bottomClass = dialerActive && dialerMinimized ? 'bottom-28' : 'bottom-28';
	const DirectionIcon = activeCall?.direction === 'inbound' ? BsTelephoneInbound : BsTelephoneOutbound;

	// What to render in the duration slot:
	// - ringing → "Klingelt…" (no counter, since nothing is being timed yet)
	// - active  → live talk time from the client timer
	// - ended   → the SDK-provided duration (authoritative), falling back to the last client value
	const durationText = getDurationText({ isRinging, isActive, isEnded, elapsed, activeCall });

	// Jump to the resolved company; minimize the overlay so it doesn't cover the target page.
	const handleSelectCompany = (companyId) => {
		setOverlayMinimized(true);
		navigate(`/crm/companies/${companyId}`);
	};

	// Primary caller name for the compact view: company name of the first match, else its
	// first contact's name. Null when nothing resolved → minimized view falls back to the number.
	const firstParty = groupLookupParties(lookup)[0] ?? null;
	const primaryName = firstParty ? (firstParty.companyName ?? firstParty.contacts[0]?.name ?? null) : null;

	if (overlayMinimized) {
		return (
			<MinimizedCallOverlay
				bottomClass={bottomClass}
				label={label}
				displayNumber={displayNumber}
				primaryName={primaryName}
				isRinging={isRinging}
				isActive={isActive}
				durationText={durationText}
				DirectionIcon={DirectionIcon}
				onExpand={() => setOverlayMinimized(false)}
			/>
		);
	}

	return (
		<ExpandedCallOverlay
			bottomClass={bottomClass}
			label={label}
			displayNumber={displayNumber}
			isRinging={isRinging}
			isActive={isActive}
			isEnded={isEnded}
			durationText={durationText}
			DirectionIcon={DirectionIcon}
			direction={activeCall?.direction}
			lookup={lookup}
			onSelectCompany={handleSelectCompany}
			widgetVisible={widgetVisible}
			onToggleWidget={() => setWidgetVisible(!widgetVisible)}
			onMinimize={() => setOverlayMinimized(true)}
		/>
	);
};

export default AircallCallOverlay;
